import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faTriangleExclamation,
  faCircleCheck,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import SearchInput from "../../ui/SearchInput";
import { AppDispatch, RootState } from "../../../store/indexRedux";
import { fetchCompanies } from "../../../store/allCompanySlice";
import type { Company } from "../../../types/companyTypes";
import type { DestinationReadiness } from "../../../api/customerSwitchTransfer";
import type { KboSearchRow } from "../../../api/company";
import { MIN_NUMBER_DIGITS, MIN_SEARCH_CHARS, useKboCompanySearch } from "../../../hooks/useKboCompanySearch";
import {
  KBO_SERVER_LIMIT,
  MAX_RESULTS,
  linkedPool,
  matchLinked,
  registerCandidates,
} from "./destinationCandidates";
import CreateCompanyStep from "./CreateCompanyStep";

export interface DestinationCompanyPickerProps {
  /** The `company_id` PK. Used ONLY to exclude A from the linked pool (PK vs PK). */
  sourceCompanyId: number;
  /**
   * 🔴 `companyNumber` — a DIFFERENT ID SPACE from `sourceCompanyId`, and the only one a
   * register row's `kbo.number` can be compared against. Keep the two visibly distinct:
   * they hold equal values for all 5 077 companies today, which is exactly what makes a
   * mix-up invisible.
   */
  sourceCompanyNumber: number;
  selected: Company | null;
  onSelect: (company: Company | null) => void;
  readiness: DestinationReadiness | null;
  readinessLoading: boolean;
  readinessError: string | null;
}

/**
 * Company B — the receiving company. TWO SOURCES, shown as TWO SECTIONS.
 *
 *   1. Companies the acting user is already a contact of (`allCompany`, i.e.
 *      `company_contact`). Selecting one is instant.
 *   2. Companies from the Belgian enterprise register (`kbo`) that are NOT yet with
 *      us. Selecting one opens an inline create — company + billing + the caller's
 *      contact link — and only then does the switch continue.
 *
 * 🔴 SECTIONS, NOT ONE BADGED LIST. The two differ in every dimension that matters:
 * next action (select vs. a form and a real write), cap semantics (a client-side 50
 * of a known pool vs. a server-side 15 of an unknown one), match semantics
 * (`includes` vs. a left-anchored `LIKE`), and loading/error state.
 *
 * The register IS populated on dev — 1 952 120 rows — so section 2 is a live surface,
 * not a placeholder. An earlier version of this comment claimed `kbo` was empty and
 * that section 2 "renders nothing"; both were wrong. What is actually true is narrower
 * and is the property the tests pin: section 2 renders NOTHING AT ALL while the search
 * is idle (`showRegisterSection = kboStatus !== "idle"`, i.e. below the 3-character
 * minimum or before the first search), and when a search SETTLES with no usable rows it
 * renders the divider, the heading and a "no new company found" line — visible chrome,
 * not silence.
 *
 * 🔴 The membership invariant is preserved, not abandoned. The old list WAS the
 * membership, so "the user is a contact of both companies" was structurally true.
 * Section 2 keeps it true by CREATING the link: the caller becomes a contact of B as
 * part of the same write that creates B.
 *
 * 🔴 `fetchCompanies` is dispatched here when the slice is empty. It is NOT loaded at
 * login — the only other dispatch sites are in SelectCompanyPage, which a reload
 * straight into this route never passes through.
 */
const DestinationCompanyPicker: React.FC<DestinationCompanyPickerProps> = ({
  sourceCompanyId,
  sourceCompanyNumber,
  selected,
  onSelect,
  readiness,
  readinessLoading,
  readinessError,
}) => {
  const { t } = useTranslation("management");
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: RootState) => state.authv2.accessToken);
  const userId = useSelector((state: RootState) => state.user.user?.id);
  const { companies, loading } = useSelector((state: RootState) => state.allCompany);
  const [search, setSearch] = useState("");

  /**
   * `"list"` or the inline create for one register row.
   *
   * Evaluated AFTER the `if (selected)` return below, exactly as the plan requires —
   * a chosen destination always wins over an in-progress create, and completing a
   * create is precisely what produces a chosen destination.
   *
   * The company/billing sub-steps live INSIDE `CreateCompanyStep` rather than here,
   * so this component never has to know how far through the form the user is.
   */
  const [creating, setCreating] = useState<KboSearchRow | null>(null);
  /**
   * How the current `selected` came to be: created here, or already ours and found
   * via the 409. `null` for an ordinary pick from section 1.
   */
  const [createdOutcome, setCreatedOutcome] = useState<{ id: number; alreadyLinked: boolean } | null>(
    null
  );

  /**
   * HARDENING — not a bug fix. Read the rule before copying this pattern.
   *
   * A re-dispatch loop of this class needs BOTH of:
   *   (a) the guard is still true after the failure, AND
   *   (b) a value in the DEP ARRAY oscillates on the failure path (changes, then
   *       changes back), which is what re-arms the effect.
   *
   * This effect has (a) but NOT (b). `allCompanySlice`'s `rejected` reducer leaves
   * `companies` untouched, so `companies.length` goes 0 → 0; `token` and `dispatch`
   * are stable; the dep array therefore never changes and React never re-runs it.
   * Measured on the pre-hardening code with the picker provably mounted: 1 call.
   * Control, with `loading` added to the deps so a dep genuinely oscillates: the run
   * never terminates.
   *
   * ⚠️ The broader rule — "the effect's own dispatch writes something in its own
   * guard or deps" — is OVER-BROAD and produces false positives; it is what flagged
   * this effect. Condition (b) is the part that does the work. The portfolio fetch in
   * CustomerSwitchTransfer is the real instance: `portfolioLoading` goes true → false
   * on rejection, and it sat in the deps.
   *
   * The ref is kept anyway: it costs nothing, and it makes the "one dispatch per
   * mount" intent explicit rather than resting on the absence of an oscillating dep,
   * which a future dep-array edit could silently reintroduce.
   */
  const companiesRequested = useRef(false);
  useEffect(() => {
    if (!token || companiesRequested.current) return;
    if (companies.length > 0) return;
    companiesRequested.current = true;
    dispatch(fetchCompanies({ token }));
  }, [companies.length, token, dispatch]);

  /**
   * The register typeahead. Debounced, aborting, and gated on a 3-character minimum
   * — see the hook for why 400ms and why its dep array must never gain `status`.
   */
  const { results: kboRows, status: kboStatus } = useKboCompanySearch(search, token ?? undefined);

  /** UNCAPPED. This is the dedupe source; see `destinationCandidates.ts`. */
  const pool = useMemo(() => linkedPool(companies, sourceCompanyId), [companies, sourceCompanyId]);
  const matches = useMemo(() => matchLinked(pool, search, MAX_RESULTS), [pool, search]);

  const { candidates, rawCount } = useMemo(
    () => registerCandidates({ rows: kboRows, pool, sourceCompanyNumber }),
    [kboRows, pool, sourceCompanyNumber]
  );

  const term = search.trim();
  const belowMinChars = term.length > 0 && term.length < MIN_SEARCH_CHARS;

  /**
   * EVENT HANDLER, never an effect. Create-then-refetch as an effect is the textbook
   * (a)+(b) loop: the guard survives a rejection and `allCompany.loading` oscillates.
   * A handler cannot be re-armed by rendering, which removes both conditions.
   *
   * The refetch is what stops the company being offered as "not with us" a second
   * time in the same session; `onSelect` is called exactly once, from here.
   */
  const handleCreated = (company: Company, alreadyLinked: boolean) => {
    setCreating(null);
    setSearch("");
    setCreatedOutcome({ id: company.id, alreadyLinked });
    if (token) dispatch(fetchCompanies({ token }));
    onSelect(company);
  };

  if (selected) {
    const justCreated = createdOutcome?.id === selected.id ? createdOutcome : null;
    return (
      <div>
        {/* ADDING → ADDED → CHECKING → READY → TRANSFER. Only the "added"
            confirmation is new here; the rest of the chain already existed. */}
        {justCreated && (
          <div className="mb-3 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm dark:bg-green-900/20 dark:border-green-800 dark:text-green-300 flex gap-2">
            <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 shrink-0" />
            <span>
              {justCreated.alreadyLinked
                ? t("customerSwitchCompanyAlreadyLinked", { companyName: selected.name })
                : t("customerSwitchCompanyCreated", { companyName: selected.name })}
            </span>
          </div>
        )}

        <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-950">
          <span>
            <span className="block text-sm font-semibold text-primary-600 dark:text-primary-300">
              {selected.name}
            </span>
            <span className="block text-xs text-primary-400 dark:text-primary-500 mt-0.5">
              {selected.reg_number}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              setCreatedOutcome(null);
              onSelect(null);
            }}
            className="text-sm font-semibold underline text-primary-500 dark:text-primary-300 shrink-0"
          >
            {t("customerSwitchChangeCompany")}
          </button>
        </div>

        {readinessLoading && (
          <p className="mt-3 text-sm text-primary-400 inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faSpinner} spin />
            {t("customerSwitchCheckingCompany")}
          </p>
        )}

        {/* An explicit `false` is a real block. `undefined` means the backend does
            not report the field yet — unknown, so the user continues and the
            backend's own refusal stays the gate. */}
        {!readinessLoading && readiness?.hasBilling === false && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 flex gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
            <span>{t("customerSwitchNoBilling", { companyName: selected.name })}</span>
          </div>
        )}

        {!readinessLoading && readiness?.hasBilling === true && (
          <p className="mt-3 text-sm text-green-700 dark:text-green-300 inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faCircleCheck} />
            {t("customerSwitchBillingOk")}
          </p>
        )}

        {!readinessLoading && readinessError && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
            <span>{readinessError}</span>
          </div>
        )}
      </div>
    );
  }

  if (creating && token) {
    return (
      <CreateCompanyStep
        row={creating}
        token={token}
        /**
         * 🔴 PASSED THROUGH UNDEFINED ON PURPOSE — do NOT coerce to "". The backend
         * 400s on a falsy `user_id`, so an empty string turns a knowable precondition
         * into a failed round trip. `CreateCompanyStep` blocks the submit and says why.
         */
        userId={userId}
        onCancel={() => setCreating(null)}
        onCreated={handleCreated}
      />
    );
  }

  const showLinkedEmpty = matches.length === 0;
  const showRegisterSection = kboStatus !== "idle";

  return (
    <div>
      <SearchInput
        className="w-full max-w-sm mb-3"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("customerSwitchDestinationSearch")}
      />

      {/* ---- Section 1: companies you are already a contact of ---- */}
      {loading && companies.length === 0 ? (
        <p className="text-sm text-primary-400 inline-flex items-center gap-2">
          <FontAwesomeIcon icon={faSpinner} spin />
          {t("loading")}
        </p>
      ) : showLinkedEmpty ? (
        <p className="text-sm text-primary-400 dark:text-primary-500">
          {/* "No matching company" became a LIE the moment a register search exists
              but has not run yet — the answer is genuinely not known below the
              minimum. Say that instead. */}
          {belowMinChars
            ? t("customerSwitchSearchMinChars", {
                min: MIN_SEARCH_CHARS,
                minDigits: MIN_NUMBER_DIGITS,
              })
            : t("customerSwitchDestinationNone")}
        </p>
      ) : (
        <>
          <ul className="space-y-1 max-h-80 overflow-y-auto">
            {matches.map((company) => (
              <li key={company.id}>
                <button
                  type="button"
                  onClick={() => {
                    setCreatedOutcome(null);
                    onSelect(company);
                  }}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
                >
                  <span className="block text-sm font-medium text-primary-600 dark:text-primary-300">
                    {company.name}
                  </span>
                  <span className="block text-xs text-primary-400 dark:text-primary-500 mt-0.5">
                    {company.reg_number}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {pool.length > matches.length && (
            <p className="mt-2 text-xs text-primary-400 dark:text-primary-500">
              {t("customerSwitchRefineSearch", { shown: matches.length, total: pool.length })}
            </p>
          )}
        </>
      )}

      {/* ---- Section 2: the enterprise register, for companies not yet with us ----
          Renders NOTHING at all while the search is idle — which, with `kbo` empty,
          is also what a completed search produces. */}
      {showRegisterSection && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-primary-800">
          <h4 className="text-sm font-semibold text-primary-600 dark:text-primary-300 mb-1">
            {t("customerSwitchRegisterLabel")}
          </h4>
          <p className="text-xs text-primary-400 dark:text-primary-500 mb-3">
            {t("customerSwitchRegisterHelp", { minDigits: MIN_NUMBER_DIGITS })}
          </p>

          {kboStatus === "searching" && (
            <p className="text-sm text-primary-400 inline-flex items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} spin />
              {t("customerSwitchRegisterSearching")}
            </p>
          )}

          {kboStatus === "error" && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
              <span>{t("customerSwitchRegisterError")}</span>
            </div>
          )}

          {kboStatus === "ready" && candidates.length === 0 && (
            <p className="text-sm text-primary-400 dark:text-primary-500">
              {t("customerSwitchRegisterNone")}
            </p>
          )}

          {kboStatus === "ready" && candidates.length > 0 && (
            <>
              <ul className="space-y-1 max-h-80 overflow-y-auto">
                {candidates.map(({ number, displayNumber, row }) => (
                  <li key={number}>
                    <button
                      type="button"
                      onClick={() => setCreating(row)}
                      className="w-full text-left p-3 rounded-lg border border-dashed border-gray-300 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span>
                          <span className="block text-sm font-medium text-primary-600 dark:text-primary-300">
                            {row.companyName}
                          </span>
                          {/* 🔴 `displayNumber`, NEVER `number`. `kbo.number` is an
                              int and cannot hold a leading zero, which 83% of the
                              register has — rendering it shows most users an
                              enterprise number that is not theirs. `number` is the
                              dedupe key and the React key only. */}
                          <span className="block text-xs text-primary-400 dark:text-primary-500 mt-0.5">
                            {displayNumber}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-semibold text-primary-500 dark:text-primary-300 inline-flex items-center gap-1">
                          <FontAwesomeIcon icon={faPlus} />
                          {t("customerSwitchAddCompanyAction")}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {/* Keyed on the PRE-DEDUPE server count. The endpoint returns no total,
                  and inventing one would be a lie — so this only says how many were
                  shown. Keying it on `candidates.length` instead would hide the hint
                  exactly when the list is most truncated. */}
              {rawCount === KBO_SERVER_LIMIT && (
                <p className="mt-2 text-xs text-primary-400 dark:text-primary-500">
                  {t("customerSwitchKboCapped", { shown: rawCount })}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DestinationCompanyPicker;
